'use client';

import axios from 'axios';
import useSWR from 'swr';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Button from '@/app/_components/buttons/Button';
import LanguageSelect from '@/app/_components/languageSelect';
const langUrl = '/api/taxonomy/languages';
const fetcher = () => axios.get(langUrl).then(({ data }) => data);

interface Props {
  step: string;
}

const OnboardingLanguage = (props: Props) => {
  const { register, control } = useFormContext();
  const { step } = props;
  const [showForm, setShowForm] = useState(false);

  const { data: languages, isLoading: isLanguagesLoading } = useSWR(
    langUrl,
    fetcher
  );

  const onShowForm = () => {
    setShowForm(true);
  };

  if (step !== 'LANGUAGE') {
    return null;
  }

  return (
    <div className="w-full md:w-[730px]">
      <p className="text-white text-2xl md:text-4xl font-semibold py-8 block">
        {showForm ? (
          <>
            <span className="text-purple3">No problem!</span> What is your
            preferred language?
          </>
        ) : (
          <>
            <span className="text-purple3">
              Zoc now translates into 25+ languages! Let&apos;s set up your
              personalized Zoc translation feature.
            </span>
            <br />
            <br />
            <span>Is English your preferred language?</span>
          </>
        )}
      </p>

      {showForm ? (
        <>
          <label
            htmlFor="language"
            className="text-white font-hanken font-semibold text-sm leading-6"
          >
            Enter your preferred language
          </label>
          <Controller
            control={control}
            name="language"
            render={({ field: { onChange, value } }) => {
              return (
                <LanguageSelect
                  variant="default"
                  {...register('language')}
                  value={value}
                  onChange={onChange}
                  languages={languages}
                  disabled={false}
                  isLanguagesLoading={isLanguagesLoading || false}
                  classNames={{
                    control: () =>
                      'rounded-[3px] border border-grey4 border-solid my-4 px-4 py-3 font-hanken text-sm leading-7 !bg-transparent',
                    singleValue: () => '!text-white',
                    input: () => '!text-white',
                    clearIndicator: () => 'data-test-clear-input',
                    menu: () => 'data-test-languages-menu',
                  }}
                />
              );
            }}
          />
        </>
      ) : null}

      <div>
        <Button
          data-test-id={showForm ? 'onboarding-next' : 'save-en-language'}
          className="!rounded-full"
          variant="primary"
          type="submit"
          label={showForm ? 'Next' : 'Yes'}
        />

        {!showForm && (
          <Button
            onClick={onShowForm}
            data-test-id="toggle-language-select"
            className="ml-3 !rounded-full"
            variant="primary"
            type="button"
            label="No"
          />
        )}
      </div>

      <p className="text-white text-xl md:text-3xl font-semibold py-8">
        Why are we asking this?
      </p>
      <p className="text-white text-sm leading-6 mb-2">
        Recordings will always be captured in the native language of the
        speaker(s).
      </p>
      <p className="text-white text-sm leading-6 mb-2">
        From there, we will automatically translate notes, and their summaries
        and topics into your preferred language. The tutor quizzes will also be
        conducted in your preferred language.
      </p>
      <p className="text-white text-sm leading-6 mb-2">
        You can always change your preferred language from the language settings
        available on your user menu!
      </p>
      <p className="text-white text-sm leading-6">
        Changing your preferred language will translate all past and future
        Zocs.
      </p>
    </div>
  );
};

export default OnboardingLanguage;
